/**
 * Searchable supplier list with checkboxes.
 * Supports typing to filter suppliers by name.
 *
 * Used by the Part Repository tab for bulk-assign and per-part-assign
 * dialogs.
 */
import { useState } from 'react';
import { Search } from 'lucide-react';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';

export interface SupplierOption {
  id: number;
  name: string;
}

interface Props {
  suppliers: SupplierOption[];
  selected: Set<number>;
  onToggle: (id: number) => void;
}

export default function SupplierSearchList({ suppliers, selected, onToggle }: Props) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = searchQuery
    ? suppliers.filter((s) =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : suppliers;

  return (
    <div className="border rounded-lg">
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Type to search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>
      <div className="max-h-[300px] overflow-y-auto scrollbar-none">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No suppliers found
          </p>
        ) : (
          filtered.map((s) => (
            <label
              key={s.id}
              className="flex items-center gap-3 px-3 py-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
            >
              <Checkbox
                checked={selected.has(s.id)}
                onCheckedChange={() => onToggle(s.id)}
              />
              <span className="text-sm">{s.name}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
